import { useState } from "react";
import { useTranslation } from "react-i18next";
import { changelogParaRol } from "../data/changelog";
import { overlayClose } from "../utils/overlayClose";
import styles from "./WhatsNewModal.module.css";

// fa-star (no fa-sparkles — ese glifo no existe en el set Solid gratuito que
// usa el sistema, por eso el círculo de "Nuevo" salía vacío).
const TIPO_CFG = {
  feature: { icon: "fa-star",              color: "var(--brand)", labelKey: "nuevo" },
  fix:     { icon: "fa-bug-slash",         color: "#4ade80", labelKey: "correcciones" },
  mejora:  { icon: "fa-wand-magic-sparkles", color: "#60a5fa", labelKey: "mejoras" },
};
// Orden de lectura: primero lo nuevo, después lo que se corrigió, al final
// los ajustes menores — en vez de mezclarlos en el orden en que quedaron
// escritos en el changelog.
const ORDEN_TIPOS = ["feature", "fix", "mejora"];

function agruparPorTipo(items) {
  const grupos = {};
  for (const it of items) {
    const tipo = TIPO_CFG[it.type] ? it.type : "mejora";
    (grupos[tipo] ??= []).push(it);
  }
  return ORDEN_TIPOS.filter((t) => grupos[t]?.length).map((t) => [t, grupos[t]]);
}

/**
 * WhatsNewModal — "¿Qué hay de nuevo?" — se muestra automáticamente al
 * iniciar sesión cuando hay una versión del changelog que el usuario no vio,
 * y se puede reabrir a mano desde el botón de Novedades en el header.
 */
export default function WhatsNewModal({ onClose }) {
  const { t } = useTranslation();
  const [ultima, ...anteriores] = changelogParaRol();
  const [verAnteriores, setVerAnteriores] = useState(false);

  if (!ultima) return null;
  const gruposUltima = agruparPorTipo(ultima.items);

  return (
    <div {...overlayClose(onClose)} style={{
      position: "fixed", inset: 0, zIndex: 4100, background: "rgba(0,0,0,0.66)",
      backdropFilter: "blur(3px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
    }}>
      <div onClick={e => e.stopPropagation()} className={styles.scrollBody} style={{
        background: "var(--bg-card)", border: "1px solid var(--border-card)", borderRadius: 16,
        width: "min(560px, 96vw)", maxHeight: "88vh",
        boxShadow: "0 28px 70px rgba(0,0,0,0.5)",
      }}>
        {/* Header */}
        <div style={{
          padding: "22px 24px 18px", borderBottom: "1px solid var(--border-card)",
          display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12,
          background: "linear-gradient(135deg, rgba(var(--brand-rgb), 0.08), transparent)",
        }}>
          <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, flexShrink: 0,
              background: "rgba(var(--brand-rgb), 0.15)", border: "1px solid rgba(var(--brand-rgb), 0.3)",
              display: "flex", alignItems: "center", justifyContent: "center" }}>
              <i className="fa-solid fa-gift" style={{ color: "var(--brand)", fontSize: "1.15rem" }} />
            </div>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <h2 style={{ margin: 0, fontSize: "1.12rem", fontWeight: 800, color: "var(--text-primary)" }}>
                  {t("whatsNew.titulo")}
                </h2>
                <span style={{ fontSize: "0.68rem", fontWeight: 700, padding: "2px 9px", borderRadius: 20,
                  background: "rgba(var(--brand-rgb), 0.15)", border: "1px solid rgba(var(--brand-rgb), 0.4)", color: "var(--brand)" }}>
                  v{ultima.version}
                </span>
              </div>
              <p style={{ margin: "3px 0 0", color: "var(--text-muted)", fontSize: "0.79rem" }}>
                {ultima.title}
              </p>
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "var(--text-muted)",
            cursor: "pointer", fontSize: "1.05rem", flexShrink: 0 }}>
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        {/* Última versión — agrupada por tipo (nuevo / correcciones / mejoras) */}
        <div style={{ padding: "18px 24px 8px", display: "flex", flexDirection: "column", gap: 16 }}>
          {gruposUltima.map(([tipo, items]) => {
            const cfg = TIPO_CFG[tipo];
            return (
              <div key={tipo}>
                <p style={{
                  margin: "0 0 9px", fontSize: "0.68rem", fontWeight: 800, letterSpacing: "0.6px",
                  textTransform: "uppercase", color: cfg.color,
                  display: "flex", alignItems: "center", gap: 6,
                }}>
                  <i className={`fa-solid ${cfg.icon}`} style={{ fontSize: "0.66rem" }} />
                  {t(`whatsNew.${cfg.labelKey}`)}
                </p>
                <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 10 }}>
                  {items.map((it, i) => (
                    <li key={i} style={{ display: "flex", gap: 11, alignItems: "flex-start" }}>
                      <span style={{ width: 5, height: 5, borderRadius: "50%", flexShrink: 0, marginTop: 8, background: cfg.color }} />
                      <span style={{ fontSize: "0.86rem", color: "var(--text-primary)", lineHeight: 1.5 }}>{it.text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Versiones anteriores — colapsadas por defecto, con desplegable */}
        {anteriores.length > 0 && (
          <div style={{ padding: "10px 24px 4px", marginTop: 10, borderTop: "1px solid var(--border-card)" }}>
            <button
              onClick={() => setVerAnteriores((v) => !v)}
              style={{
                width: "100%", background: "none", border: "none", cursor: "pointer",
                padding: "10px 0", display: "flex", alignItems: "center", justifyContent: "space-between",
              }}
            >
              <span style={{ fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.6px",
                textTransform: "uppercase", color: "var(--text-muted)" }}>
                {t("whatsNew.versionesAnteriores")} ({anteriores.length})
              </span>
              <i className={`fa-solid fa-chevron-${verAnteriores ? "up" : "down"}`}
                style={{ color: "var(--text-muted)", fontSize: "0.72rem" }} />
            </button>

            {verAnteriores && (
              <div style={{ display: "flex", flexDirection: "column", gap: 14, padding: "4px 0 16px" }}>
                {anteriores.map(rel => (
                  <div key={rel.version}>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 5 }}>
                      <span style={{ fontSize: "0.78rem", fontWeight: 700, color: "var(--text-secondary)" }}>v{rel.version}</span>
                      <span style={{ fontSize: "0.72rem", color: "var(--text-muted)" }}>{rel.title}</span>
                    </div>
                    <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 4 }}>
                      {rel.items.map((it, i) => (
                        <li key={i} style={{ fontSize: "0.78rem", color: "var(--text-muted)", paddingLeft: 14, position: "relative" }}>
                          <span style={{ position: "absolute", left: 0 }}>·</span>{it.text}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div style={{ display: "flex", justifyContent: "flex-end", padding: "14px 24px",
          borderTop: "1px solid var(--border-card)" }}>
          <button onClick={onClose} style={{
            padding: "9px 22px", background: "var(--brand)", border: "none", borderRadius: 9,
            color: "#fff", fontSize: "0.86rem", fontWeight: 700, cursor: "pointer",
          }}>
            {t("whatsNew.entendido")}
          </button>
        </div>
      </div>
    </div>
  );
}
